/**
 * The words, switches and section numbers of the logged-out homepage's refreshed parts.
 *
 * The page is the chambers-ledger landing with a few parts taken from Sathish's "iAdvocate
 * Homepage Refresh Implementation Brief" (2026-11-01), as Ajay chose them on 2026-11-01: the
 * header, the practice-tab hero, "Three practices. One chamber.", the FAQ, the closing call to
 * action and the footer — and the Trust Center, which opens as a full-page slide view over the
 * homepage with the policy documents as its tabs (Ajay, 2026-11-03), behind a slim Trust strip
 * near the footer. Their copy lives here; the older bands (film, searches, stats, workspace,
 * pricing) keep theirs in their own components.
 *
 * backend/tests/test_landing_claims.py reads this file: a coming-soon practice may never
 * offer a trial, and only Criminal is live.
 *
 * PUBLICATION GATES. The brief allows gated copy in code but forbids showing it "until the
 * stated condition passes in production and the product owner enables it". Copy that
 * depends on a gate is only ever produced through the functions below, which take the gates
 * as an argument (tests pass other values; the page uses GATES).
 */
import { BUSINESS } from '../../../constants/businessDetails';

// ─── Publication gates ──────────────────────────────────────────────────────

export type GateKey =
  | 'whatsapp'
  | 'documentGeneration'
  | 'productDataBoundary'
  | 'indiaHosting'
  | 'tenantSeparation';

export type Gates = Readonly<Record<GateKey, boolean>>;

/** Every gate is OFF. Each comment gives the brief's condition and why it is not met
 * (checked 2026-11-01). Switching one on publishes a claim to advocates. */
export const GATES: Gates = {
  // "WhatsApp communication". Condition: message send, consent, opt-out, template,
  // delivery-status and failure-path testing. WhatsApp is switched off in production and
  // the live Privacy Policy says iAdvocate sends no automated WhatsApp messages.
  whatsapp: false,
  // "document preparation" and drafting. Condition: Draft Documents loads from the sidebar
  // and the matter context; generation, unresolved fields, preview, save and export pass.
  documentGeneration: false,
  // "verified legal data … not general open-web answers". Condition: no advertised workflow
  // falls back to open-web results. The FIR analyser's special-Acts agent
  // (backend/app/agents/special_acts_lawyer.py) searches the open web in production.
  productDataBoundary: false,
  // "Data hosted in India". Condition: production data, backups and subprocessors are all in
  // scope. Cloud SQL backups set no location, and Privacy Policy §3.1 sends matter content to
  // Gemini, Gemini File Search and BigQuery.
  indiaHosting: false,
  // "…with organisational separation inside the service". Condition: authentication,
  // authorisation and tenant-separation controls confirmed. Held until the tenant-separation
  // issue reported on 2026-10-18 is fixed.
  tenantSeparation: false,
};

/** "a, b and c" — the house list style, used for every sentence assembled from parts. */
export function joinList(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

// ─── Section numbers ────────────────────────────────────────────────────────

export type SectionId = 'film' | 'practices' | 'searches' | 'inside' | 'pricing' | 'faq';

export interface PageSection {
  /** The section element's id — the anchor every link to it uses. */
  id: SectionId;
  /** The small label beside the section's heading, without its number. */
  label: string;
  /** The hero's table of contents. */
  title: string;
  sub: string;
}

/**
 * The numbered sections, in page order. A section's number is its place in this list, so
 * adding, removing or moving one renumbers every label and the hero's contents together.
 */
export const PAGE_SECTIONS: PageSection[] = [
  { id: 'film', label: 'THE FILM', title: 'The criminal case flow', sub: 'One matter, FIR to hearing, narrated' },
  { id: 'practices', label: 'PRACTICE AREAS', title: 'Three practices', sub: 'Criminal in beta; Civil and Corporate coming soon' },
  { id: 'searches', label: 'ASKED & ANSWERED', title: 'Asked and answered', sub: 'The searches advocates already make, answered' },
  { id: 'inside', label: 'THE WORKSPACE', title: 'A day inside iAdvocate', sub: 'The criminal daily docket' },
  { id: 'pricing', label: 'SCHEDULE OF FEES', title: 'Pricing', sub: 'Pro and Max, and the trial in plain terms' },
  { id: 'faq', label: 'FAQ', title: 'FAQ', sub: 'Trial, data, availability and cancellation' },
];

/** "03" for the third section. */
export const sectionNumber = (index: number): string => String(index + 1).padStart(2, '0');

/** "03 · ASKED & ANSWERED" — the label a section shows beside its heading. */
export function sectionLabel(id: SectionId): string {
  const index = PAGE_SECTIONS.findIndex((s) => s.id === id);
  return `${sectionNumber(index)} · ${PAGE_SECTIONS[index].label}`;
}

// ─── Actions ────────────────────────────────────────────────────────────────

/** One primary label everywhere (brief). Sign-in is secondary and never as heavy. */
export const CTA = {
  trial: 'Start Free Trial',
  signIn: 'Sign in with Google',
  notify: 'Notify me',
} as const;

// ─── Practices ──────────────────────────────────────────────────────────────

export type PracticeKey = 'criminal' | 'civil' | 'corporate';
export type PracticeStatus = 'beta_live' | 'coming_soon';

/** ONE wording for a status wherever it shows — tabs, Product menu, cards, footer. If
 * "Cooking" reads too informal at design review, the brief's alternative is
 * "In development. Coming soon."; change it here only. */
export const STATUS_LABEL: Record<PracticeStatus, string> = {
  beta_live: 'Beta Live',
  coming_soon: 'Cooking. Coming soon.',
};

export interface Practice {
  key: PracticeKey;
  tab: string;
  name: string;
  status: PracticeStatus;
  /** Card copy, and the whole teaser for a coming-soon tab. */
  card: string;
  /** The hero's vertical marginalia for this tab. */
  marginalia: string;
}

export const PRACTICES: Practice[] = [
  {
    key: 'criminal',
    tab: 'Criminal',
    name: 'Criminal Practice',
    status: 'beta_live',
    card: 'The integrated criminal-practice workspace for matters, FIR analysis, case theory, research, hearings and documents.',
    marginalia: 'BNS · BNSS · BSA · 2023 CODES READY',
  },
  {
    key: 'civil',
    tab: 'Civil',
    name: 'Civil Practice',
    status: 'coming_soon',
    card: 'A connected workspace for pleadings, evidence, hearings and civil-matter preparation is in development.',
    marginalia: 'CIVIL PRACTICE · IN DEVELOPMENT',
  },
  {
    key: 'corporate',
    tab: 'Corporate',
    name: 'Corporate Practice',
    status: 'coming_soon',
    card: 'A structured workspace for corporate matters, contracts, compliance and advisory work is in development.',
    marginalia: 'CORPORATE PRACTICE · IN DEVELOPMENT',
  },
];

/** "CRIMINAL PRACTICE • BETA LIVE" — the status line above a hero panel. */
export const statusLine = (p: Practice): string =>
  `${p.name} • ${STATUS_LABEL[p.status]}`.toUpperCase();

/** "Notify me" is an email to the general enquiries address, not a sign-up form: a form
 * would collect personal data and need its own consent copy (a form is planned — owner,
 * 2026-11-03). A coming-soon practice never routes into the app. */
export const notifyMeHref = (p: Practice): string =>
  `mailto:${BUSINESS.contactEmail}?subject=${encodeURIComponent(`Notify me: ${p.name}`)}`;

// ─── Header ─────────────────────────────────────────────────────────────────

export interface PageLink {
  label: string;
  href: string;
}

export const NAV_PRODUCT_LABEL = 'Product';
export const NAV_EXPLORE: PageLink = { label: 'Explore Product', href: '#inside' };
export const NAV_LINKS: PageLink[] = [
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Trust Center', href: '#trust' },
  { label: 'Contact Us', href: '/contact' },
];

// ─── Hero ───────────────────────────────────────────────────────────────────

export const HERO = {
  headlineLead: 'One intelligent workspace for',
  headlineEmphasis: 'the complete criminal practice.',
} as const;

/**
 * The criminal tab's supporting copy. With every gate on it is the brief's text exactly;
 * a gated item drops out of the list, and the open-web sentence only appears with
 * productDataBoundary (it is the claim that gate exists for).
 */
export function heroSupportingCopy(g: Gates = GATES): string {
  const tail = [
    ...(g.whatsapp ? ['WhatsApp communication'] : []),
    ...(g.documentGeneration ? ['document preparation'] : []),
  ];
  const items = [
    'matters',
    'FIR analysis',
    g.productDataBoundary ? 'verified legal data' : 'legal research',
    'intelligent routing and alerts',
    ...tail,
  ];
  // "intelligent routing and alerts" carries its own "and": with nothing after it the list
  // ends there; with one item after it a serial comma keeps "…alerts, and WhatsApp…" readable.
  let list: string;
  if (tail.length === 0) list = items.join(', ');
  else if (tail.length === 1) list = `${items.slice(0, -1).join(', ')}, and ${tail[0]}`;
  else list = joinList(items);
  const lead = `An integrated, AI-powered workspace for criminal advocates — connecting ${list} in one place.`;
  return g.productDataBoundary
    ? `${lead} Work from the matter record and product-governed legal sources, not general open-web answers.`
    : lead;
}

/** The short trust strip under the hero's actions. "Encrypted", not "Encrypted in transit"
 * (Ajay, 2026-11-01). */
export function heroTrustStrip(g: Gates = GATES): string[] {
  return [
    'Encrypted',
    ...(g.indiaHosting ? ['Data hosted in India'] : []),
    'DPDP compliance assessment in progress',
    'Made in India for Indian law',
  ];
}

export const CONTENTS_LABEL = 'TABLE OF CONTENTS';

// ─── Three practices ────────────────────────────────────────────────────────

export const PRACTICES_SECTION = {
  headingLead: 'Three practices.',
  headingEmphasis: 'One chamber.',
} as const;

// ─── Trust Center ───────────────────────────────────────────────────────────

export interface TrustTopic {
  topic: string;
  statement: string;
}

/** The brief's "publishable wording", each only while its evidence holds. */
export function trustTopics(g: Gates = GATES): TrustTopic[] {
  return [
    { topic: 'Encryption', statement: 'Data is encrypted in transit.' },
    ...(g.indiaHosting ? [{ topic: 'Data residency', statement: 'Matter data is hosted in India.' }] : []),
    { topic: 'DPDP', statement: 'DPDP compliance assessment in progress.' },
    ...(g.tenantSeparation
      ? [{
          topic: 'Access',
          statement: 'Matter access is controlled by account, with organisational separation inside the service.',
        }]
      : []),
    {
      topic: 'AI training',
      statement: 'Matter content is not used to train third-party foundation models under the current paid API arrangement.',
    },
    { topic: 'Professional responsibility', statement: 'AI assists. The advocate decides and verifies the final work.' },
  ];
}

export const TRUST_SECTION = {
  heading: 'Trust Center',
  intro: 'How matter data is handled today, stated plainly — with work that is still in progress labelled as such.',
  /** The slim strip near the footer that opens the slide view. */
  open: 'Open Trust Center',
  contact: 'Contact Us',
  back: 'Back to home',
} as const;

/**
 * The documents the Trust Center slide view opens as tabs, in tab order. On the homepage a
 * link to one never leaves the page: TrustCenterSlide slides the Trust Center in over it, open
 * at that tab (Ajay, 2026-11-01 and 2026-11-03). The links still point at the standalone pages
 * (/terms …), which stay for Razorpay's website review, for a link opened in a new tab and for
 * the in-app links; the Trust Center itself has no page of its own (#trust opens it). A key is
 * also the URL hash the open view writes, so /#privacy is a link to the Privacy tab.
 */
export type TrustDocKey = 'trust' | 'terms' | 'privacy' | 'refund-policy' | 'shipping-policy' | 'contact';

export const TRUST_DOCS: { key: TrustDocKey; tab: string; href: string }[] = [
  { key: 'trust', tab: 'Trust Center', href: '#trust' },
  { key: 'terms', tab: 'Terms', href: '/terms' },
  { key: 'privacy', tab: 'Privacy', href: '/privacy' },
  { key: 'refund-policy', tab: 'Cancellation & Refund', href: '/refund-policy' },
  { key: 'shipping-policy', tab: 'Shipping & Delivery', href: '/shipping-policy' },
  { key: 'contact', tab: 'Contact Us', href: '/contact' },
];

/** The Trust Center tab a link opens — "/terms" or "#terms" both open Terms — or null. */
export function trustDocFor(href: string | null | undefined): TrustDocKey | null {
  const slug = /^[#/]([a-z-]+)$/.exec(href ?? '')?.[1];
  return TRUST_DOCS.find((d) => d.key === slug)?.key ?? null;
}

/** Published addresses — the same ones the Privacy Policy and Contact page give (owner,
 * 2026-11-03; privacy@ is no longer used — privacy questions go to support). */
export const CONTACT_EMAILS = {
  contact: BUSINESS.contactEmail,
  support: BUSINESS.supportEmail,
  accounts: BUSINESS.accountsEmail,
  legal: BUSINESS.legalEmail,
  grievance: BUSINESS.grievanceEmail,
} as const;

export interface TrustCenterSection {
  id: string;
  title: string;
  paragraphs: string[];
  links?: PageLink[];
}

export const TRUST_CENTER = {
  title: 'Trust Center',
  intro: 'What iAdvocate can state today about your data, the controls around it and the limits of its AI — with anything still in progress labelled as such. Our Privacy Policy is the governing document.',
} as const;

/**
 * The Trust Center document (its tab). Wording follows the brief's trust table and the live
 * Privacy Policy. Until the indiaHosting gate's evidence is complete the residency section
 * states no storage location at all — Cloud SQL backups are not pinned to asia-south1, and
 * the Privacy Policy names no region — only a status label and the processors the policy
 * itself lists (§3.1).
 */
export function trustCenterSections(g: Gates = GATES): TrustCenterSection[] {
  return [
    {
      id: 'data-handling',
      title: 'Data handling and India residency',
      paragraphs: g.indiaHosting
        ? ['Matter data is hosted in India.', 'Matter content is processed only to provide the features you use.']
        : [
            'India data residency: being verified across production storage, backups and the services that process matter content.',
            'To provide AI analysis and legal search, the relevant matter content is sent to Google’s Gemini API, Gemini File Search and Google BigQuery, as our Privacy Policy describes.',
            'Matter content is processed only to provide the features you use.',
          ],
    },
    {
      id: 'encryption-access',
      title: 'Encryption and access controls',
      paragraphs: [
        'Data is encrypted in transit.',
        ...(g.tenantSeparation
          ? ['Matter access is controlled by account, with organisational separation inside the service.']
          : []),
        'You sign in with your Google account, verified through Firebase Authentication.',
      ],
    },
    {
      id: 'ai-providers',
      title: 'AI providers, subprocessors and training boundaries',
      paragraphs: [
        'Matter content is not used to train third-party foundation models under the current paid API arrangement. Our AI processing runs on Google’s Gemini API under a paid subscription.',
        'Sub-processors: Google Firebase (authentication), Google Gemini API and File Search (AI analysis and search), Google Cloud Storage (document vault), Google BigQuery (judgment search) and our payment gateway (billing).',
        'We do not sell your personal data or your clients’ data.',
        'AI assists. The advocate decides and verifies the final work.',
      ],
    },
    {
      id: 'retention',
      title: 'Retention, deletion and account closure',
      paragraphs: [
        'Account data is kept while your account is active, and matter content for as long as you keep it in iAdvocate.',
        'You can delete individual matter content or your whole account at any time. Associated personal data is then deleted or anonymised within a reasonable period, except where the law requires us to keep it, such as billing records.',
      ],
    },
    {
      id: 'dpdp',
      title: 'DPDP readiness and assessment status',
      paragraphs: [
        'DPDP compliance assessment in progress.',
        'For your clients’ personal data that you upload, you are the Data Fiduciary under the DPDP Act and Kovai Digi Tech acts as your Data Processor.',
        'You may access, correct or erase your personal data, withdraw consent, nominate someone to exercise these rights, and raise a grievance with our Grievance Officer or, if it is not resolved, the Data Protection Board of India.',
      ],
    },
    {
      id: 'contacts',
      title: 'Incident reporting, support and grievance contacts',
      paragraphs: [
        `Support, privacy or security concerns: ${CONTACT_EMAILS.support}`,
        `Billing, payments and refunds: ${CONTACT_EMAILS.accounts}`,
        `Grievance Officer: ${CONTACT_EMAILS.grievance}`,
        `Legal: ${CONTACT_EMAILS.legal}`,
      ],
    },
    {
      id: 'policies',
      title: 'Terms, Privacy Policy, Cancellation and Refund Policy and Contact',
      paragraphs: [],
      links: [
        { label: 'Terms of Service', href: '/terms' },
        { label: 'Privacy Policy', href: '/privacy' },
        { label: 'Cancellation and Refund Policy', href: '/refund-policy' },
        { label: 'Shipping and Delivery Policy', href: '/shipping-policy' },
        { label: 'Contact', href: '/contact' },
      ],
    },
  ];
}

// ─── FAQ ────────────────────────────────────────────────────────────────────

export const FAQ_HEADING = 'Frequently asked questions';

export interface FaqItem {
  q: string;
  a: string;
}

export function faqItems(g: Gates = GATES): FaqItem[] {
  const connects = joinList([
    'matter records',
    'legal analysis',
    'research',
    'alerts',
    ...(g.whatsapp ? ['communication'] : []),
    'documents',
  ]);
  const assists = joinList([
    'reading',
    'organising',
    'researching',
    'alerting',
    ...(g.documentGeneration ? ['drafting'] : []),
  ]);
  return [
    {
      q: 'What is iAdvocate?',
      a: `iAdvocate is an AI-assisted practice workspace for Indian advocates. It connects ${connects} around the same case context.`,
    },
    {
      q: 'Who is the live product for?',
      a: 'Criminal Practice is available in beta for advocates working on Indian criminal matters. Civil Practice and Corporate Practice are in development and will be introduced separately.',
    },
    ...(g.productDataBoundary
      ? [{
          q: 'Does iAdvocate use general web results?',
          a: 'The product is designed to work from the matter record and legal data maintained within iAdvocate rather than relying on uncontrolled open-web answers. The source and coverage are shown wherever a legal result is presented.',
        }]
      : []),
    {
      q: 'Can iAdvocate replace an advocate’s judgment?',
      a: `No. iAdvocate assists with ${assists}. The advocate must verify the facts, law, citations, strategy and final work product.`,
    },
    ...(g.indiaHosting
      ? [{
          q: 'Where is my data hosted?',
          a: 'Matter data is hosted in India. The Trust Center sets out what this covers.',
        }]
      : []),
    {
      q: 'How does the free trial work?',
      a: 'The Criminal Practice Pro trial lasts 30 days, requires no card and includes 1 GB of trial storage. Because no card is taken, the trial does not automatically become a paid subscription.',
    },
    {
      q: 'Can I cancel a paid plan?',
      a: 'A monthly recurring plan can be cancelled to stop future renewals, with access continuing until the end of the paid period. Annual plans end after twelve months without automatic renewal.',
    },
    {
      q: 'Are Civil and Corporate Practice available?',
      a: 'Not yet. Criminal Practice is Beta Live. Civil and Corporate are in development and will be announced when they are ready for use.',
    },
  ];
}

// ─── Closing call to action ─────────────────────────────────────────────────

export const CLOSING = {
  headline: 'Stop searching and documenting. Start strategizing and preparing.',
  copy: 'Bring the matter, analysis, research, alerts and preparation into one connected criminal-practice workspace — and spend more time on the decisions that need an advocate.',
} as const;

// ─── Footer ─────────────────────────────────────────────────────────────────

/** Every footer link stays on the homepage: the policies open in the Trust Center slide view
 * (trustDocFor), though each still names its standalone page. */
export const FOOTER = {
  groups: { product: 'Product', explore: 'Explore', legal: 'Legal', operator: 'Operator', contact: 'Contact' },
  explore: [
    { label: 'Pricing', href: '#pricing' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Trust Center', href: '#trust' },
    { label: 'Contact Us', href: '/contact' },
  ] as PageLink[],
  // Shipping & Delivery stays linked: Razorpay's website review checks for it before it
  // enables UPI Autopay, cards and Subscriptions (2026-10-18).
  legal: [
    { label: 'Terms', href: '/terms' },
    { label: 'Privacy', href: '/privacy' },
    { label: 'Cancellation & Refund', href: '/refund-policy' },
    { label: 'Shipping & Delivery', href: '/shipping-policy' },
  ] as PageLink[],
  operator: `${BUSINESS.legalName} • Coimbatore, Tamil Nadu, India • GSTIN ${BUSINESS.gstin}`,
  contacts: [
    { label: 'Support', email: CONTACT_EMAILS.support },
    { label: 'Billing', email: CONTACT_EMAILS.accounts },
    { label: 'Legal', email: CONTACT_EMAILS.legal },
  ],
  legalWording:
    'iAdvocate is an AI-assisted legal technology service. It is not a law firm and does not provide legal advice. Advocates must independently verify outputs against the matter record and primary legal sources before use.',
} as const;
